import axios, { type AxiosResponse, type CreateAxiosDefaults } from 'axios';

import { captureError } from './capture-error';
import {
  GENERIC_ERROR_MESSAGE,
  IGNORED_ERROR_CODES,
  IGNORED_ERROR_STATUSES,
} from './constants';

// The layer both HTTP clients share: wire format, response unwrapping and the
// shape of every error they throw. Neither adapter imports the other, so the
// server client never evaluates the browser instance and its refresh state
// machine.
export const JSON_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

// Browser-side config. It carries no credential: the browser reaches the API
// through the same-origin proxy, which stamps the platform key server-side so
// the studio's login cookie is first-party.
export const BASE_AXIOS_CONFIG: CreateAxiosDefaults = {
  baseURL: '/api',
  headers: JSON_HEADERS,
};

// Every caller in the codebase expects unwrapped data, so the envelope is
// stripped here rather than at each call site.
export const unwrapResponse = (response: AxiosResponse) =>
  response.data?.data ?? response.data;

// Endpoints that produce expected failures (session checks, token refresh).
// Network errors on these are not bugs — skip PostHog capture for them.
const AUTH_ENDPOINTS = ['/public/auth/refresh', '/public/users/me'] as const;

const isAuthEndpoint = (url?: string): boolean =>
  !!url && AUTH_ENDPOINTS.some((ep) => url.includes(ep));

// The whole sign-in surface, not just the two above: a 401 here means wrong
// credentials rather than an expired session, and a 2xx here is what clears the
// bailout latch. Named once so moving or renaming a route cannot leave the
// latch permanently set, which would stop every 401 in the tab being retried.
const AUTH_ROUTE_PREFIX = '/public/auth/';

export const isAuthRoute = (url?: string): boolean =>
  !!url && url.includes(AUTH_ROUTE_PREFIX);

// Best-effort beacons (marketing pings): failures are expected — ad-blockers,
// navigation cancellation — and not actionable. Skip PostHog capture.
const BEACON_ENDPOINTS = ['/public/tracking'] as const;

const isBeaconEndpoint = (url?: string): boolean =>
  !!url && BEACON_ENDPOINTS.some((ep) => url.includes(ep));

// The one shape a caller ever receives. `.name` carries the backend's error
// code (LOGIN_REQUIRED and friends) and `.status` the HTTP status, because raw
// axios errors never reach service catch blocks. The code always wins, on the
// session check too: a 403 there can be the studio going dark, and callers
// must be able to read that. An auth-endpoint failure that carries no code — a
// refresh 401, a network fault — is named AuthError, which PostHog's
// before_send filters on; a coded one is filtered by its code instead.
// `.details` carries the backend's structured fields as sent, unvalidated (a
// retry time, or the current slug in a TENANT_MOVED answer), so a caller never
// has to read them out of the message text.
export const toHandledError = (error: any, fallbackMessage: string) => {
  const endpoint = error?.config?.url || '';
  const errorCode = error?.response?.data?.code;

  const thrown = new Error(error?.response?.data?.message || fallbackMessage);
  if (errorCode) {
    thrown.name = errorCode;
  } else if (isAuthEndpoint(endpoint)) {
    thrown.name = 'AuthError';
  }
  (thrown as any)._httpHandled = true;
  (thrown as any).status = error?.response?.status;
  (thrown as any).details = error?.response?.data?.details;
  return thrown;
};

// Applied after the report, never before: captureError drops anything already
// stamped, so stamping first would silence the very report this marks.
export const markCaptured = (error: Error): Error => {
  (error as any)._httpCaptured = true;
  return error;
};

// Paths only, never the validator's messages, which can quote the value sent.
// Other VALIDATION_FAILED answers carry a message and no list.
const refusedFields = (data: { errors?: { field: string }[] } | undefined) =>
  Array.isArray(data?.errors) ? data.errors.map((err) => err.field) : undefined;

// Ref :: https://axios-http.com/docs/handling_errors
export function handleError(error: any): never {
  // Already processed by the interceptor — prevent double-capture when
  // service catch blocks re-call handleError on the thrown Error.
  if (error?._httpHandled) throw error;

  if (axios.isAxiosError(error)) {
    if (error.response) {
      const status = error.response.status;
      const endpoint = error.config?.url || 'unknown';
      const method = error.config?.method?.toUpperCase() || 'unknown';

      const errorCode = error.response.data?.code;
      const isIgnoredError = errorCode && IGNORED_ERROR_CODES.includes(errorCode);

      const shouldCapture =
        status >= 400 &&
        !IGNORED_ERROR_STATUSES.includes(status) &&
        !isAuthEndpoint(endpoint) &&
        !isBeaconEndpoint(endpoint) &&
        !isIgnoredError;

      const thrown = toHandledError(error, GENERIC_ERROR_MESSAGE);

      if (shouldCapture) {
        // The shaped error, not a fresh one. Its `name` carries the backend's
        // code, so PostHog groups the failure under that code — and because
        // captureError drops anything already reported, a fresh `new Error`
        // here meant every coded refusal was filed as one anonymous "Error"
        // issue and the caller's own richer report was thrown away.
        captureError({
          error: thrown,
          source: 'api',
          severity: status >= 500 ? 'error' : 'warning',
          context: {
            endpoint,
            method,
            status_code: status,
            error_code: errorCode,
            fields: refusedFields(error.response.data),
          },
        });
        markCaptured(thrown);
      }

      throw thrown;
    } else if (error.request) {
      const endpoint = error.config?.url || 'unknown';

      // Skip network errors on auth endpoints — expected when session expires
      const shouldCapture =
        !isAuthEndpoint(endpoint) && !isBeaconEndpoint(endpoint);

      if (shouldCapture) {
        captureError({
          error: new Error(`Network error: ${error.message}`),
          source: 'api',
          severity: 'error',
          context: { type: 'network_error', endpoint },
        });
      }

      // Through toHandledError like the branch above, not a second hand-rolled
      // Error: the old form read `error.request.status`, which only exists on a
      // browser XMLHttpRequest, so every server timeout and refused connection
      // threw "Request failed with status code undefined" and carried neither
      // `.status` nor a code for callers to branch on.
      const thrown = toHandledError(error, GENERIC_ERROR_MESSAGE);
      throw shouldCapture ? markCaptured(thrown) : thrown;
    }
  }

  // Also catches axios errors carrying neither a response nor a request
  // (request-setup failures, cancellations). Falling through without throwing
  // would resolve the promise, handing callers undefined data and no error.
  captureError({
    error,
    source: 'api',
    severity: 'error',
    context: { type: 'request_setup_error' },
  });

  const errorMessage = error?.toJSON?.()
    ? error.toJSON().message
    : error.message || GENERIC_ERROR_MESSAGE;
  throw markCaptured(toHandledError(error, errorMessage));
}
